import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";

import { useAuth } from "../contexts/AuthContext";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponse } from "../models/system";
import { GetTillConfigResponse } from "../models/till/ITillConfig";
import { GetTillQuoteResponse, ITillQuoteRequest } from "../models/till/ITillQuote";
import { GetTillReceiptResponse } from "../models/till/ITillReceipt";
import { GetTillSaleResponse, ITillCheckoutRequest, ITillRefundRequest, TillPaymentStatus } from "../models/till/ITillSale";
import { GetTillStockSearchResponse } from "../models/till/ITillStockSearch";
import apiConfig from "../utils/apiConfig";
import { shouldPollSale } from "../utils/till/paymentStatus";

export const tillKeys = {
  config: ["till", "config"] as const,
  sale: (saleId: string) => ["till", "sale", saleId] as const,
  receipt: (saleId: string) => ["till", "sale", saleId, "receipt"] as const,
  quote: (userId: string | undefined, signature: string, req: ITillQuoteRequest) => ["till", "quote", userId, signature, req] as const,
  stockSearch: (query: string) => ["till", "stockSearch", query] as const,
};

/** Endpoints the till page talks to. Basket add/remove/update stay in useBaskets. */
export default function useTill(componentName = "useTill") {
  const { get, post, getFile } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();
  const { userId } = useAuth();

  const config = useQuery({
    queryKey: tillKeys.config,
    queryFn: () => get<GetTillConfigResponse>("/till/config"),
    staleTime: 1000 * 60 * 5,
  });

  const invalidateAfterSale = () => {
    queryClient.invalidateQueries(["basket", userId]);
    queryClient.invalidateQueries(["parts"]);
    queryClient.invalidateQueries(["pagedTable"]);
    queryClient.invalidateQueries(["till", "stockSearch"]);
    queryClient.invalidateQueries(["invoices"]);
  };

  const checkout = useMutation(
    (body: ITillCheckoutRequest) => post<ITillCheckoutRequest, GetTillSaleResponse>(`/till/${userId}/checkout`, body),
    {
      onSuccess: (res) => {
        if (res?.data?.saleId) queryClient.setQueryData(tillKeys.sale(res.data.saleId), res);
        invalidateAfterSale();
      },
    }
  );

  const cancelSale = useMutation(
    (saleId: string) => post<Record<string, never>, GetTillSaleResponse>(`/till/sale/${saleId}/cancel`, {}),
    {
      onSuccess: (res, saleId) => {
        queryClient.setQueryData(tillKeys.sale(saleId), res);
        invalidateAfterSale();
      },
    }
  );

  const retrySale = useMutation(
    (saleId: string) => post<Record<string, never>, GetTillSaleResponse>(`/till/sale/${saleId}/retry`, {}),
    {
      onSuccess: (res, saleId) => {
        queryClient.setQueryData(tillKeys.sale(saleId), res);
      },
    }
  );

  const refundSale = useMutation(
    ({ saleId, body }: { saleId: string; body: ITillRefundRequest }) => post<ITillRefundRequest, GetTillSaleResponse>(`/till/sale/${saleId}/refund`, body),
    {
      onSuccess: (res, { saleId }) => {
        queryClient.setQueryData(tillKeys.sale(saleId), res);
        invalidateAfterSale();
      },
    }
  );

  const displayOnReader = useMutation(
    ({ readerId, body }: { readerId: string; body: ITillQuoteRequest }) => post<ITillQuoteRequest, DefaultResponse>(`/till/${userId}/reader/${readerId}/display`, body)
  );

  const clearReaderDisplay = useMutation(
    (readerId: string) => post<Record<string, never>, DefaultResponse>(`/till/reader/${readerId}/clear-display`, {})
  );

  const downloadInvoicePdf = async (invoiceId: string, fileName: string) => {
    const file = await getFile(`/invoice/${invoiceId}/download`);
    const url = window.URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return {
    config,
    checkout,
    cancelSale,
    retrySale,
    refundSale,
    displayOnReader,
    clearReaderDisplay,
    downloadInvoicePdf,
    /** A card sale completes on a later poll, not on the checkout call, so the page calls this then. */
    invalidateAfterSale,
  };
}

/**
 * Server-side totals for the current basket. `basketSignature` changes whenever a line is added,
 * removed or repriced, so the quote follows the basket without touching useBaskets.
 */
export function useTillQuote(req: ITillQuoteRequest, basketSignature: string, enabled: boolean, componentName = "useTillQuote") {
  const { post } = useApiHelper(apiConfig(componentName));
  const { userId } = useAuth();

  return useQuery({
    queryKey: tillKeys.quote(userId, basketSignature, req),
    queryFn: () => post<ITillQuoteRequest, GetTillQuoteResponse>(`/till/${userId}/quote`, req),
    enabled: enabled && !!userId,
    keepPreviousData: true,
    staleTime: 1000 * 30,
  });
}

/** Unsold stock matching the search box, from the till's own ranked lookup (exact tag first). */
export function useTillStockSearch(query: string, take = 24, componentName = "useTillStockSearch") {
  const { get } = useApiHelper(apiConfig(componentName));
  const debounced = useDebounce(query.trim(), 250);

  return useQuery({
    queryKey: tillKeys.stockSearch(debounced),
    queryFn: () => get<GetTillStockSearchResponse>(`/till/search?query=${encodeURIComponent(debounced)}&take=${take}`),
    enabled: debounced.length >= 2,
    keepPreviousData: true,
    staleTime: 1000 * 30,
  });
}

/** One sale, polled every 1.5s while the reader still has the card. */
export function useTillSale(saleId: string | undefined, componentName = "useTillSale") {
  const { get } = useApiHelper(apiConfig(componentName));

  return useQuery({
    queryKey: tillKeys.sale(saleId ?? ""),
    queryFn: () => get<GetTillSaleResponse>(`/till/sale/${saleId}`),
    enabled: !!saleId,
    staleTime: 0,
    refetchInterval: (data) => (shouldPollSale(data?.data?.payment?.status as TillPaymentStatus | undefined) ? 1500 : false),
    refetchIntervalInBackground: true,
  });
}

export function useTillReceipt(saleId: string | undefined, enabled = true, componentName = "useTillReceipt") {
  const { get } = useApiHelper(apiConfig(componentName));

  return useQuery({
    queryKey: tillKeys.receipt(saleId ?? ""),
    queryFn: () => get<GetTillReceiptResponse>(`/till/sale/${saleId}/receipt`),
    enabled: enabled && !!saleId,
    staleTime: Infinity,
  });
}
